import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import AddIcon from '@mui/icons-material/Add';
import RecentEvents from "./RecentEvents";

const StyledBox = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  width: '100%',
  height: 400,
  marginTop: theme.spacing(8),
  borderRadius: (theme.vars || theme).shape.borderRadius,
  outline: '6px solid',
  outlineColor: 'hsla(220, 25%, 80%, 0.2)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.grey[200],
  boxShadow: '0 0 12px 8px hsla(220, 25%, 80%, 0.2)',
  backgroundImage: `url(${process.env.TEMPLATE_IMAGE_URL || 'https://mui.com'}/static/screenshots/material-ui/getting-started/templates/dashboard.jpg)`,
  backgroundSize: 'cover',
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(10),
    height: 700,
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 24px 12px hsla(210, 100%, 25%, 0.2)',
    backgroundImage: `url(${process.env.TEMPLATE_IMAGE_URL || 'https://mui.com'}/static/screenshots/material-ui/getting-started/templates/dashboard-dark.jpg)`,
    outlineColor: 'hsla(220, 20%, 42%, 0.1)',
    borderColor: (theme.vars || theme).palette.grey[700],
  }),
}));

export default function Hero() {
  const [eventCode, setEventCode] = useState("");
  const [setEventDetails] = useState(null);
  const [setEventDocId] = useState(null);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  
  useEffect(() => {
    if (eventCode) {
      fetchEvent(eventCode);
    }
  },[eventCode]);
  
  const handleCheckEventCode = async () => {
    try{
      setError(""); // Reset error state
      const eventsRef = collection(db, "events");
      const q = query(eventsRef, where("eventCode", "==", eventCode));
      const querySnapshot = await getDocs(q);
  
      if (querySnapshot.empty) {
        setError("Invalid event code. Please try again.");
        return;
      }
  
      const eventDoc = querySnapshot.docs[0];
      const documentID = eventDoc.id;
  
      // Redirect to the event URL
        navigate(`join/${eventCode}`);
      } catch (err) {
        console.error("Error checking event code:", err);
        setError("Something went wrong. Please try again later.");
    }
  };
        
          const fetchEvent = async (code) => {
          try {
            setError('');
            setEventDetails(null);
            setEventDocId(null);
      
            // Find the event document by eventCode
            const eventsRef = collection(db, 'events');
            const eventQuery = query(eventsRef, where('eventCode', '==', code));
            const eventSnapshot = await getDocs(eventQuery);
      
            if (!eventSnapshot.empty) {
              // Assume the first matching event document
              const eventDoc = eventSnapshot.docs[0];
              const eventData = eventDoc.data();
      
              setEventDocId(eventDoc.id);  // Store document ID for post retrieval
              setEventDetails({
                eventImage: eventData.eventImageURL,
                eventName: eventData.eventName,
                eventCode: eventData.eventCode,
                eventOwnerName: eventData.eventOwnerName,
              });
            } else {
              setError('No event found with that event code.');
            }
          } catch (err) {
            console.error("Error fetching event:", err);
            setError('Failed to retrieve event data.');
          }
        };

  const btm = {    bottom: '0',    position: 'absolute'};
      
  return (
    <Box
      id="hero"
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
        backgroundRepeat: 'no-repeat',
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)',
        ...theme.applyStyles('dark', {
          backgroundImage:
            'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)',
        }),
      })}
    >
      <Container
        sx={{
          height: '1005',
          display: 'flex',
          margin: 'auto',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <Stack
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'center', width: { xs: '100%', sm: '70%' } }}
        >
          <Typography
            variant="h6"
            sx={{textAlign: 'center',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              fontSize: 'clamp(3rem, 10vw, 3.5rem)',
            }}>
            Discover. Connect. Celebrate.                        
          </Typography>
          <Typography variant="body2"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              width: { sm: '100%', md: '80%' },
            }}
          >
            Celebrate special moments with your loved ones.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            useFlexGap
            sx={{ pt: 2, width: { xs: '100%', sm: '450px' } }}
          >
            <Button
              variant="contained"
              color="primary"              
              display={eventCode.length !== 6}
              size="medium"
              sx={{ minWidth: 'fit-content' }}                           
              startIcon={<AddIcon />              
              }
              onClick={() => navigate(`/admin/create`)}
            >
              New event
            </Button>
            {/* <InputLabel htmlFor="email-hero" sx={visuallyHidden}>
              Email
            </InputLabel> */}            
            <TextField error
              id="email-hero"
              hiddenLabel
              size="medium"
              variant="outlined"
              aria-label="Enter your event code"
              placeholder="Your event code"
              helperText={eventCode.length>6? "Event code should not be more than 6 digits.":""}
              value={eventCode}
              onChange={(e) => setEventCode(e.target.value)}
              fullWidth
              maxLength="6"
              slotProps={{                
                input: {
                  autoComplete: 'off',
                  'aria-label': 'Enter your event code',
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyboardIcon />
                    </InputAdornment>
                  ),
                },                
              }}
            />
            <Button
              variant="text"
              color="primary"
              disabled={eventCode.length !== 6}
              size="medium"
              sx={{ minWidth: 'fit-content' }}             
              onClick={handleCheckEventCode}>
              Join now
            </Button>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
            >
            By clicking &quot;Join now&quot; you agree to our&nbsp;
            <Link href="#" color="primary">
              Terms & Conditions
            </Link>
            .
          </Typography>
        </Stack>
        {/* <StyledBox id="image" /> */}

        <RecentEvents style={btm}/>

      </Container>
    </Box>
  );
}
